import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Download, FileText, RefreshCw, ExternalLink, Check, Share2, CalendarClock, CheckCircle2, Receipt, MessageSquare, Copy, DollarSign, ShieldCheck, Repeat, CalendarDays, Loader2, CreditCard, AlertCircle, Send } from 'lucide-react';
import { auth } from '../firebase';
import { Card } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter,
  DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import PrintableQuote from '../components/PrintableQuote';
import { getQuote } from '../features/quotes/useQuotes';
import { useCompany } from '../context/CompanyContext';
import { useTasks } from '../context/TasksContext';
import { useLeads } from '../context/LeadsContext';
import { getDisplayName } from '../lib/utils';
import { normalizeQuote } from '../utils/normalizeQuote';
import { toast } from 'sonner';
import { seedFirstRecurringJobIfNeeded } from '../utils/servicePlanSeeding';
import { activateServicePlanFromQuote } from '../utils/activateServicePlanFromQuote';
import CardOnFile from './CardOnFile';
import CardSetupModal from './CardSetupModal';
import ChargeNowModal from '../components/ChargeNowModal';

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  try {
    const date = typeof value === 'string' ? new Date(value) :
                 value.toDate ? value.toDate() : new Date(value);
    return date.toLocaleString('en-US');
  } catch {
    return 'N/A';
  }
};

const formatAddress = (address) => {
  if (!address) return 'N/A';
  if (typeof address === 'string') return address;
  const parts = [];
  if (address.line1) parts.push(address.line1);
  if (address.city) parts.push(address.city);
  if (address.state) parts.push(address.state);
  if (address.zip) parts.push(address.zip);
  return parts.join(', ') || 'N/A';
};

// 30-minute time slots 6:00 AM → 7:30 PM
const TIME_SLOTS = Array.from({ length: 28 }, (_, i) => {
  const totalMins = 360 + i * 30;
  const hour = Math.floor(totalMins / 60);
  const min = totalMins % 60;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return {
    label: `${h12}:${String(min).padStart(2, '0')} ${ampm}`,
    value: `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`,
  };
});

const DURATION_OPTIONS = [
  { label: '1 hour', value: 60 },
  { label: '1.5 hours', value: 90 },
  { label: '2 hours', value: 120 },
  { label: '2.5 hours', value: 150 },
  { label: '3 hours', value: 180 },
  { label: '4 hours', value: 240 },
];

const getTomorrowDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

const formatScheduleDisplay = (dateStr, timeStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [h, min] = timeStr.split(':').map(Number);
  const dt = new Date(y, m - 1, d, h, min);
  return (
    dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) +
    ' at ' +
    dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  );
};

/**
 * QuoteDetail - View a single quote
 * 
 * Supports two URL patterns:
 * - /quotes/:quoteId?companyId=xxx (new top-level format)
 * - /quotes/:leadId/:quoteId (legacy format, companyId from context)
 */
const QuoteDetail = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { companyId: contextCompanyId } = useCompany();
  const { tasks, addTask, updateTask, completeJob } = useTasks();
  const { updateLead, leads } = useLeads();
  
  // Support both URL patterns
  const quoteId = params.quoteId || params.leadId; // If only one param, it's quoteId
  const legacyLeadId = params.quoteId ? params.leadId : null; // Only if both params present
  const companyId = searchParams.get('companyId') || contextCompanyId;
  
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showPlanConfirm, setShowPlanConfirm] = useState(false);

  // Scheduling modal
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [schedDate, setSchedDate] = useState(getTomorrowDate);
  const [schedTime, setSchedTime] = useState('09:00');
  const [schedDuration, setSchedDuration] = useState(120); // minutes
  const [scheduling, setScheduling] = useState(false);

  // Job completion
  const [completing, setCompleting] = useState(false);

  // Invoice
  const [invoice, setInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);

  // Prepay charge
  const [prepayInvoice, setPrepayInvoice]         = useState(null);
  const [chargingPrepay, setChargingPrepay]       = useState(false);
  const [showPrepayCollect, setShowPrepayCollect] = useState(false);
  const [chargeNowOpen,    setChargeNowOpen]    = useState(false);

  // Existing job task for this lead (used to decide create vs update)
  const existingJobTask = useMemo(
    () => tasks.find(t => t.leadId === quote?.leadId && t.type === 'job'),
    [tasks, quote?.leadId],
  );

  /**
   * Contractor approves the quote internally (marks as approved by contractor).
   * Phase 2: if quote has an active recurringPlan, copies it to leads/{id}.recurrence
   * and seeds the first recurring job task so it appears immediately in the scheduler.
   *
   * HARDENING GUARDS (prevents completedOccurrences reset on re-approval):
   *   Guard 1 — skip Phase 2 if the quote was already 'approved' before this action
   *   Guard 2 — skip Phase 2 if the lead already has recurrence.active === true
   *
   * SAFE WRITE — uses dotted Firestore paths so completedOccurrences / lastJobDate /
   * nextJobDate are never touched (only interval, unit, planLabel, totalOccurrences,
   * startDate, and active are written).
   */

  // Gate: if quote has an active recurring plan, show confirmation before approving
  const onApproveClick = () => {
    const plan = quote?.recurringPlan;
    if (plan?.active && quote?.status !== 'approved') {
      setShowPlanConfirm(true);
    } else {
      handleApproveQuote();
    }
  };

  const handleApproveQuote = async () => {
    if (!companyId || !quoteId) return;

    // Snapshot mutable values before any async operation — React state closures
    // keep a stale reference after setQuote fires, so we capture upfront.
    const quoteStatusBefore = quote?.status;     // 'draft' | 'sent' | 'approved'
    const plan               = quote?.recurringPlan;
    const pkgBefore          = quote?.prepayPackage;
    const leadId             = quote?.leadId;

    setApproving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const params = new URLSearchParams({ companyId });
      const response = await fetch(`${backendUrl}/api/quotes/${quoteId}/approve?${params}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await response.json();

      if (data.success) {
        setQuote(prev => ({
          ...prev,
          status: 'approved',
          approvedAt: data.approvedAt,
          approvedBy: 'contractor',
        }));
        toast.success('Quote approved successfully');

        // ── Phase 2: Recurring plan activation ──────────────────────────────
        if (plan?.active && leadId) {

          // Guard 1: Re-approval — quote was already approved before this click.
          // Prevents repeated calls from resetting completedOccurrences.
          if (quoteStatusBefore === 'approved') {
            console.log(
              '[handleApproveQuote] Phase 2 skipped — quote was already approved (no recurrence write)',
              { quoteId, leadId }
            );

          // Guard 2: Lead already has an active service plan.
          // First-time approval sets the plan; subsequent quote approvals must not overwrite.
          } else if (leads?.find(l => l.id === leadId)?.recurrence?.active === true) {
            console.log(
              '[handleApproveQuote] Phase 2 skipped — lead already has active recurrence',
              { leadId }
            );

          } else {
            // Sprint 304 (Phase 2 closure): single-writer path. Route through
            // the shared helper which POSTs to /api/leads/{id}/enrollments.
            // Enrollment is authoritative; legacy `lead.recurrence` mirror is
            // populated server-side when the lead has no primary enrollment.
            try {
              console.log(
                '[handleApproveQuote] Activating service plan via enrollment endpoint',
                { leadId, interval: plan.interval, planLabel: plan.planLabel }
              );
              const result = await activateServicePlanFromQuote(
                companyId,
                leadId,
                plan,
                {
                  quoteId,   // Phase 1: preserve billing lineage
                  // Snapshot recurring tax terms from the quote's own totals.
                  taxEnabled: (quote?.totals?.taxRate || 0) > 0,
                  taxRate: quote?.totals?.taxRate ?? null,
                },
              );
              if (result.activated) {
                toast.success('Service plan activated — first job added to scheduler');
              } else if (result.reason === 'already_active') {
                console.log('[handleApproveQuote] Skipped — enrollment already active', { leadId });
              }
            } catch (planErr) {
              console.error('[handleApproveQuote] Service plan setup failed:', planErr);
              // Non-fatal — quote approval already succeeded
              toast.error('Quote approved, but service plan setup failed. Set it manually from the lead profile.');
            }
          }
        }

        // ── Phase 3: Prepay package activation ──────────────────────────────
        // Guard: only write on first-time approval AND only if lead doesn't
        // already have an active package (prevents double-activation).
        if (pkgBefore?.active && leadId) {
          if (quoteStatusBefore === 'approved') {
            console.log(
              '[handleApproveQuote] Phase 3 skipped — quote was already approved',
              { quoteId, leadId }
            );
          } else if (leads?.find(l => l.id === leadId)?.prepayPackage?.active === true) {
            console.log(
              '[handleApproveQuote] Phase 3 skipped — lead already has active prepay package',
              { leadId }
            );
          } else {
            try {
              console.log(
                '[handleApproveQuote] Writing prepayPackage to lead via dotted-path merge',
                { leadId, visitCount: pkgBefore.visitCount, packageName: pkgBefore.packageName }
              );
              await updateLead(leadId, {
                'prepayPackage.active':                 true,
                'prepayPackage.packageName':            pkgBefore.packageName ?? null,
                'prepayPackage.totalCoveredVisits':     pkgBefore.visitCount  ?? 0,
                'prepayPackage.remainingCoveredVisits': pkgBefore.visitCount  ?? 0,
                'prepayPackage.packagePrice':           pkgBefore.packagePrice ?? null,
                'prepayPackage.activatedAt':            new Date().toISOString(),
                'prepayPackage.sourceQuoteId':          quoteId,
              });
              toast.success(`Prepay package activated — ${pkgBefore.visitCount ?? 0} visits covered`);
            } catch (pkgErr) {
              console.error('[handleApproveQuote] Prepay package setup failed:', pkgErr);
              // Non-fatal
              toast.error('Quote approved, but prepay package setup failed. Activate it manually from the lead profile.');
            }
          }
        }
      } else {
        toast.error(data.message || data.detail || 'Failed to approve quote');
      }
    } catch (err) {
      console.error('[handleApproveQuote] Error:', err);
      toast.error('Failed to approve quote');
    } finally {
      setApproving(false);
    }
  };

  /**
   * Schedule a job from the approved quote.
   * Mirrors the DnD scheduling logic: creates/updates a job Task.
   * Lead stage is NOT updated — job.status is the operational source of truth.
   */
  const handleScheduleJob = async () => {
    if (!schedDate || !schedTime || !quote?.leadId) return;
    setScheduling(true);
    try {
      const [year, month, day] = schedDate.split('-').map(Number);
      const [hour, min] = schedTime.split(':').map(Number);
      const start = new Date(year, month - 1, day, hour, min, 0, 0);
      const end = new Date(start.getTime() + schedDuration * 60 * 1000);
      const leadId = quote.leadId;

      // Derive a job title from the quote
      const clientName = getDisplayName(quote.client, 'Job');
      const serviceLabel = normalizeQuote(quote).lineItems?.[0]?.name || 'Service';
      const jobTitle = `${clientName} — ${serviceLabel}`;

      if (existingJobTask) {
        await updateTask(existingJobTask.id, {
          scheduledStart: start.toISOString(),
          scheduledEnd: end.toISOString(),
          status: 'scheduled',
        });
      } else {
        await addTask({
          leadId,
          title: jobTitle,
          type: 'job',
          status: 'scheduled',
          serviceType: leads.find(l => l.id === leadId)?.service || '',
          scheduledStart: start.toISOString(),
          scheduledEnd: end.toISOString(),
        });
      }

      // Lead stage stays 'Booked' — job.status drives the operational lifecycle.

      // Send SMS confirmation to customer (best-effort, non-blocking)
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const token = await auth.currentUser?.getIdToken(false);
        await fetch(`${backendUrl}/api/quotes/${quoteId}/schedule-sms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            companyId,
            scheduledStart: start.toISOString(),
            scheduledDisplayText: formatScheduleDisplay(schedDate, schedTime),
          }),
        });
      } catch (smsErr) {
        console.warn('[handleScheduleJob] SMS notification failed (non-blocking):', smsErr);
      }

      toast.success('Job scheduled!');
      setScheduleOpen(false);
    } catch (err) {
      console.error('[handleScheduleJob] Error:', err);
      toast.error('Failed to schedule job. Please try again.');
    } finally {
      setScheduling(false);
    }
  };

  /**
   * Copy the public customer-facing quote link to clipboard.
   * Mints a fresh HMAC-signed short-lived token via the backend so forwarded
   * links cannot be approved indefinitely.
   */
  const handleCopyLink = async () => {
    try {
      const { getAuth } = await import('firebase/auth');
      const idToken = await getAuth().currentUser?.getIdToken(false);
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(
        `${API}/api/quotes/${quoteId}/public-link?companyId=${encodeURIComponent(companyId)}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      if (!res.ok) throw new Error('mint_failed');
      const data = await res.json();
      await navigator.clipboard.writeText(data.url);
      toast.success('Secure quote link copied — expires in 7 days');
    } catch (err) {
      console.error('[handleCopyLink] Failed to mint signed link, falling back:', err);
      // Graceful fallback: copy the legacy unsigned link so the flow never fails outright.
      const origin = window.location.origin;
      const legacyLink = `${origin}/q/${quoteId}`;
      try {
        await navigator.clipboard.writeText(legacyLink);
        toast.success('Quote link copied to clipboard');
      } catch {
        toast.error('Failed to copy link');
      }
    }
  };

  /**
   * Mark the scheduled job task as completed.
   * Lead stage is NOT updated — job.status is the operational source of truth.
   * Sprint 4: routes through the canonical completeJob helper so the
   * backend recurrence engine + invoice pipeline runs as one transaction.
   */
  const handleMarkComplete = async () => {
    if (!existingJobTask || !quote?.leadId) return;
    setCompleting(true);
    try {
      await completeJob(existingJobTask.id, {
        quoteId: quote.quoteId || quoteId,
        originUrl: window.location.origin,
      });
      toast.success('Job marked as complete!');
    } catch (err) {
      console.error('[handleMarkComplete] Error:', err);
      toast.error('Failed to mark job complete', {
        description: err?.message || 'Please retry — recurring scheduling depends on this.',
      });
    } finally {
      setCompleting(false);
    }
  };

  /**
   * Create an invoice + Stripe Checkout Session for this quote.
   */
  const handleCreateInvoice = async () => {
    if (!quoteId || !companyId) return;
    setCreatingInvoice(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(`${backendUrl}/api/invoices/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          companyId,
          quoteId,
          taskId: existingJobTask?.id || null,
          originUrl: window.location.origin,
        }),
      });
      const data = await res.json();
      if (data.success && data.invoice) {
        setInvoice(data.invoice);
        toast.success('Invoice created! Payment link is ready to send.');
      } else {
        toast.error(data.detail || data.message || 'Failed to create invoice');
      }
    } catch (err) {
      console.error('[handleCreateInvoice] Error:', err);
      toast.error('Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  /**
   * Charge the full season upfront — creates a Stripe Checkout for the prepay bundle
   * and sends SMS. The webhook activates prepayPackage on the lead when payment is received.
   */
  const handleChargePrepay = async () => {
    const pkg = quote?.prepayPackage;
    if (!pkg?.visitCount || !pkg?.packagePrice || !companyId || !quoteId) return;
    setChargingPrepay(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(`${backendUrl}/api/invoices/create-prepay`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          companyId,
          leadId:       quote.leadId,
          quoteId,
          visitCount:   pkg.visitCount,
          packagePrice: pkg.packagePrice,
          packageName:  pkg.packageName || 'Season Package',
          originUrl:    `${window.location.origin}/quotes/${quoteId}?companyId=${companyId}`,
          sendSms:      true,
        }),
      });
      const data = await res.json();
      if (data.success && data.invoice) {
        setPrepayInvoice(data.invoice);
        if (data.alreadyExists) {
          toast.success('Season package invoice already created — link copied.');
        } else {
          toast.success(`Season package invoice created! Payment link sent via SMS.`);
        }
      } else {
        toast.error(data.detail || data.message || 'Failed to create season package invoice');
      }
    } catch (err) {
      console.error('[handleChargePrepay] Error:', err);
      toast.error('Failed to create season package invoice');
    } finally {
      setChargingPrepay(false);
    }
  };

  /**
   * Send the invoice payment link to the customer via Telnyx SMS.
   */
  const handleSendInvoiceSms = async () => {
    if (!invoice?.invoiceId) return;
    setSendingSms(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(`${backendUrl}/api/invoices/${invoice.invoiceId}/send-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Payment link sent via SMS!');
      } else {
        // Normalize raw API errors into a user-friendly message
        const errMsg = data.message || '';
        if (errMsg.includes('403') || errMsg.includes('Telnyx') || errMsg.includes('API Error')) {
          toast.error('SMS service unavailable. Please check your Telnyx configuration.');
        } else if (errMsg.includes('phone')) {
          toast.error('No customer phone number on file for this quote.');
        } else {
          toast.error(errMsg || 'Failed to send SMS');
        }
      }
    } catch (err) {
      console.error('[handleSendInvoiceSms] Error:', err);
      toast.error('Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };

  /**
   * Copy the Stripe payment link to clipboard.
   */
  const handleCopyPaymentLink = () => {
    if (!invoice?.stripePaymentLink) return;
    navigator.clipboard.writeText(invoice.stripePaymentLink).then(
      () => toast.success('Payment link copied to clipboard!'),
      () => toast.error('Failed to copy link'),
    );
  };

  /**
   * Generate or regenerate PDF for the quote
   */
  const handleGeneratePdf = async () => {
    if (!companyId || !quoteId) return;
    
    setPdfGenerating(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = await auth.currentUser?.getIdToken(false);
      const response = await fetch(`${backendUrl}/api/quotes/pdf/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ companyId, quoteId }),
      });
      
      const data = await response.json();
      if (data.success) {
        // Update local state with new PDF URL
        setQuote(prev => ({
          ...prev,
          pdfUrl: data.pdfUrl,
          pdfGeneratedAt: data.pdfGeneratedAt,
          pdfVersion: data.pdfVersion,
        }));
        toast.success('PDF generated successfully');
      } else {
        toast.error(data.message || 'Failed to generate PDF');
      }
    } catch (err) {
      console.error('[handleGeneratePdf] Error:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setPdfGenerating(false);
    }
  };

  /**
   * Open PDF in new tab
   */
  const handleViewPdf = () => {
    if (quote?.pdfUrl) {
      window.open(quote.pdfUrl, '_blank');
    } else {
      toast.info('Generating PDF...');
      handleGeneratePdf();
    }
  };

  /**
   * Download PDF
   */
  const handleDownloadPdf = async () => {
    if (!quote?.pdfUrl) {
      toast.info('Generating PDF...');
      await handleGeneratePdf();
      return;
    }
    
    try {
      const response = await fetch(quote.pdfUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quote-${(quote.quoteId || quoteId)?.slice(-8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('[handleDownloadPdf] Error:', err);
      // Fallback: open in new tab
      window.open(quote.pdfUrl, '_blank');
    }
  };

  useEffect(() => {
    if (!quoteId || !companyId) {
      setError('Missing quote or company ID');
      setLoading(false);
      return;
    }

    let cancelled = false;
    
    const fetchQuote = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const res = await getQuote({ 
          leadId: legacyLeadId, 
          clientId: null, 
          quoteId,
          companyId 
        });
        
        if (cancelled) return;
        
        if (!res.success || !res.quote) {
          setError('Quote not found');
          setQuote(null);
        } else {
          setQuote(res.quote);
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load quote');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchQuote();

    return () => {
      cancelled = true;
    };
  }, [quoteId, companyId, legacyLeadId]);

  // Fetch existing invoices for this quote
  useEffect(() => {
    if (!quoteId || !companyId) return;
    let cancelled = false;
    const fetchInvoice = async () => {
      setInvoiceLoading(true);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const token = await auth.currentUser?.getIdToken(false);
        const params = new URLSearchParams({ companyId });
        const res = await fetch(`${backendUrl}/api/invoices/by-quote/${quoteId}?${params}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data.success && data.invoices?.length > 0) {
          // Separate prepay vs. standard invoices
          const all = data.invoices;
          const pi  = all.find(i => i.invoiceType === 'prepay');
          const std = all.find(i => i.invoiceType !== 'prepay') || all[0];
          if (pi) setPrepayInvoice(pi);
          if (std && std.invoiceType !== 'prepay') setInvoice(std);
          else if (!pi) setInvoice(all[0]);
        }
      } catch (err) {
        console.warn('[QuoteDetail] fetchInvoice error:', err);
      } finally {
        if (!cancelled) setInvoiceLoading(false);
      }
    };
    fetchInvoice();
    return () => { cancelled = true; };
  }, [quoteId, companyId]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Card className="p-8 text-center">
          <p className="text-gray-500">{error || 'Quote not found'}</p>
        </Card>
      </div>
    );
  }

  const client = quote.client || {};
  const normalizedQuote = normalizeQuote(quote);
  const lineItems = normalizedQuote.lineItems || [];
  const services = quote.services || {};   // kept for PrintableQuote (PDF) — do not remove
  const totals = quote.totals || {};

  // Build client info for printable component
  const clientInfoForPrintable = {
    firstName: client.firstName || '',
    lastName: client.lastName || '',
    address: formatAddress(client.address),
    city: client.address?.city || '',
    state: client.address?.state || '',
    zip: client.address?.zip || '',
    phone: client.phone || '',
    email: client.email || '',
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      booked: 'bg-purple-100 text-purple-700',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${styles[status] || styles.draft}`}>
        {status || 'draft'}
      </span>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4 md:space-y-6 pb-32 md:pb-6">
      {/* Header - Mobile: Stacked, Desktop: Inline */}
      <div className="space-y-3 md:space-y-0 md:flex md:items-center md:justify-between">
        <div className="flex items-center gap-3 md:gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">Quote Details</h1>
            <p className="text-xs md:text-sm text-gray-500">
              <span className="font-mono">#{(quote.quoteId || quoteId)?.slice(-8)}</span>
              {' · '}
              {getStatusBadge(quote.status)}
            </p>
          </div>
        </div>
        
        {/* Desktop actions - hidden on mobile */}
        <div className="hidden md:flex items-center gap-2">
          {/* Mark Job Complete — when approved, job task exists, not yet completed */}
          {quote.status === 'approved' && existingJobTask && existingJobTask.status !== 'completed' && (
            <Button
              variant="default"
              size="sm"
              onClick={handleMarkComplete}
              disabled={completing}
              data-testid="mark-complete-btn"
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {completing
                ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {completing ? 'Completing...' : 'Mark Job Complete'}
            </Button>
          )}
          {/* Create Invoice — when job is done and no invoice yet */}
          {existingJobTask?.status === 'completed' && !invoice && !invoiceLoading && (
            <Button
              variant="default"
              size="sm"
              onClick={handleCreateInvoice}
              disabled={creatingInvoice}
              data-testid="create-invoice-btn"
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {creatingInvoice
                ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                : <Receipt className="w-4 h-4 mr-2" />}
              {creatingInvoice ? 'Creating...' : 'Create Invoice'}
            </Button>
          )}
          {/* Approve Quote — only shown when not yet approved */}
          {quote.status !== 'approved' && (
            <Button
              variant="default"
              size="sm"
              onClick={onApproveClick}
              disabled={approving}
              data-testid="approve-quote-btn"
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {approving
                ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                : <Check className="w-4 h-4 mr-2" />}
              {approving ? 'Approving...' : 'Approve Quote'}
            </Button>
          )}
          {/* Schedule Job — only when approved and lead is linked */}
          {quote.status === 'approved' && quote.leadId && (
            <Button
              variant="default"
              size="sm"
              onClick={() => setScheduleOpen(true)}
              data-testid="schedule-job-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <CalendarClock className="w-4 h-4 mr-2" />
              {existingJobTask?.scheduledStart ? 'Reschedule Job' : 'Schedule Job'}
            </Button>
          )}
          {/* Share public quote link */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyLink}
            data-testid="copy-quote-link-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Copy Link
          </Button>
          {quote.pdfUrl ? (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleViewPdf}
                data-testid="view-pdf-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View PDF
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownloadPdf}
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleGeneratePdf}
                disabled={pdfGenerating}
                title="Regenerate PDF"
                data-testid="regenerate-pdf-btn"
              >
                <RefreshCw className={`w-4 h-4 ${pdfGenerating ? 'animate-spin' : ''}`} />
              </Button>
            </>
          ) : (
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleGeneratePdf}
              disabled={pdfGenerating}
              data-testid="generate-pdf-btn"
            >
              {pdfGenerating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              {pdfGenerating ? 'Generating...' : 'Generate PDF'}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Mobile Sticky Bottom Action Bar - positioned above MobileBottomNav */}
      <div className="md:hidden fixed bottom-14 left-0 right-0 bg-white border-t border-gray-200 p-3 z-40">
        <div className="flex items-center justify-center gap-2 max-w-md mx-auto">
          {/* Approve — only when not yet approved */}
          {quote.status !== 'approved' && (
            <Button
              size="sm"
              className="flex-1 h-11 bg-green-600 hover:bg-green-700 text-white"
              onClick={onApproveClick}
              disabled={approving}
              data-testid="approve-quote-btn-mobile"
            >
              {approving
                ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                : <Check className="w-4 h-4 mr-2" />}
              {approving ? 'Approving...' : 'Approve'}
            </Button>
          )}
          {/* Schedule Job — when approved and lead linked */}
          {quote.status === 'approved' && quote.leadId && (
            <Button
              size="sm"
              className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setScheduleOpen(true)}
              data-testid="schedule-job-btn-mobile"
            >
              <CalendarClock className="w-4 h-4 mr-2" />
              {existingJobTask?.scheduledStart ? 'Reschedule' : 'Schedule'}
            </Button>
          )}
          {/* Copy public link */}
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-11"
            onClick={handleCopyLink}
            data-testid="copy-quote-link-btn-mobile"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Copy Link
          </Button>
          {quote.pdfUrl ? (
            <Button
              size="sm"
              className="flex-1 h-11 text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              onClick={handleDownloadPdf}
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
          ) : (
            <Button
              size="sm"
              className="flex-1 h-11 text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              onClick={handleGeneratePdf}
              disabled={pdfGenerating}
            >
              {pdfGenerating
                ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                : <FileText className="w-4 h-4 mr-2" />}
              PDF
            </Button>
          )}
        </div>
      </div>

      {/* Approved banner */}
      {quote.status === 'approved' && (
        <Card className="p-4 border-green-200 bg-green-50">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <Check className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-green-800 text-sm">Quote Approved</p>
              <p className="text-xs text-green-600">
                Approved by {quote.approvedBy || 'contractor'}
                {quote.approvedAt ? ` · ${formatDateTime(quote.approvedAt)}` : ''}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Client Info */}
      <Card className="p-3 md:p-4">
        <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">Client Information</h2>
        <Separator className="mb-2 md:mb-3" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-500 text-xs">Name</span>
            <p className="text-gray-900">{getDisplayName(client, 'No name provided')}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500 text-xs">Phone</span>
            <p className="text-gray-900">{client.phone || 'N/A'}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500 text-xs">Email</span>
            <p className="text-gray-900 break-all">{client.email || 'N/A'}</p>
          </div>
          <div>
            <span className="font-medium text-gray-500 text-xs">Address</span>
            <p className="text-gray-900">{formatAddress(client.address)}</p>
          </div>
        </div>
      </Card>

      {/* Services — unified rendering for both Calculator and Workspace quotes */}
      {lineItems.length > 0 && (
        <Card className="p-3 md:p-4">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">Services</h2>
          <Separator className="mb-2 md:mb-3" />
          <div className="space-y-2 md:space-y-3">
            {lineItems.map((item, index) => (
              <div key={index} className="flex justify-between items-start py-2 border-b last:border-0 gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 text-sm md:text-base">{item.name}</p>
                  {item.description && (
                    <p className="text-xs md:text-sm text-gray-500">{item.description}</p>
                  )}
                </div>
                <p className="font-semibold text-gray-900 shrink-0">
                  ${((item.total ?? item.price ?? item.unitPrice) || 0).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Totals */}
      <Card className="p-3 md:p-4">
        <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-3">Summary</h2>
        <Separator className="mb-2 md:mb-3" />
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Subtotal</span>
            <span className="text-gray-900">${(totals.subtotal || 0).toFixed(2)}</span>
          </div>
          {totals.discount > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Discount</span>
              <span className="text-green-600">-${(totals.discount || 0).toFixed(2)}</span>
            </div>
          )}
          {totals.tax > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-500">Tax</span>
              <span className="text-gray-900">${(totals.tax || 0).toFixed(2)}</span>
            </div>
          )}
          <Separator />
          <div className="flex justify-between text-base md:text-lg font-semibold">
            <span className="text-gray-900">Total</span>
            <span className="text-gray-900">${(totals.grandTotal || 0).toFixed(2)}</span>
          </div>
        </div>
      </Card>

      {/* Metadata */}
      <div className="text-xs text-gray-400 text-right pb-4">
        Created: {formatDateTime(quote.createdAt)}
        {quote.source && ` · Source: ${quote.source}`}
      </div>

      {/* ── Season Package / Prepay Charge Section ───────────────────────── */}
      {quote.status === 'approved' && quote.prepayPackage?.visitCount > 0 && quote.prepayPackage?.packagePrice > 0 && (
        <Card className="p-4 space-y-3" data-testid="prepay-charge-section">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Receipt className="w-4 h-4 text-indigo-500" />
              Season Package
            </h2>
            {prepayInvoice?.status === 'paid' && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
                Paid
              </span>
            )}
            {prepayInvoice?.status === 'sent' && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">
                Awaiting Payment
              </span>
            )}
          </div>
          <Separator />

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-800">
                {quote.prepayPackage.packageName || 'Season Package'}
              </p>
              <p className="text-xs text-gray-500">
                {quote.prepayPackage.visitCount} visits ·{' '}
                ${Number(quote.prepayPackage.packagePrice).toFixed(2)} upfront
              </p>
            </div>

            {/* Already paid — show link copy only */}
            {prepayInvoice?.status === 'paid' && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-green-600 font-medium">Package activated</span>
              </div>
            )}

            {/* Invoice sent — show payment link + copy */}
            {prepayInvoice?.status === 'sent' && prepayInvoice?.stripePaymentLink && (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="copy-prepay-link-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(prepayInvoice.stripePaymentLink)
                      .then(() => toast.success('Payment link copied'))
                      .catch(() => toast.error('Failed to copy'));
                  }}
                  className="h-7 text-xs"
                >
                  Copy Link
                </Button>
              </div>
            )}

            {/* Not yet charged — show two paths */}
            {!prepayInvoice && (
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {/* Primary: operator collects card right now (no client action needed) */}
                <Button
                  size="sm"
                  onClick={() => setShowPrepayCollect(true)}
                  data-testid="collect-prepay-now-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                >
                  <CreditCard className="w-3 h-3 mr-1" />
                  Collect Payment Now
                </Button>

                {/* Secondary: send Stripe Checkout link via SMS */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleChargePrepay}
                  disabled={chargingPrepay}
                  data-testid="charge-prepay-btn"
                  className="shrink-0 text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                >
                  {chargingPrepay
                    ? <RefreshCw className="w-4 h-4 animate-spin" />
                    : <Send className="w-3 h-3 mr-1" />}
                  {chargingPrepay ? 'Sending…' : 'Send Payment Link'}
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ── Card on File Section — hidden when a prepay package is active on this quote ──────────── */}
      {quote.status === 'approved' && quote.leadId && !(quote.prepayPackage?.visitCount > 0) && (
        <Card className="p-4 space-y-3" data-testid="card-on-file-section">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-gray-500" />
              Card on File
            </h2>
            <span className="text-xs text-gray-400">· for future billing</span>
          </div>
          <Separator />
          <CardOnFile
            companyId={companyId}
            leadId={quote.leadId}
            leadPhone={quote?.client?.phone || null}
          />
        </Card>
      )}

      {/* Job Completion & Invoice Section */}
      {quote.status === 'approved' && (existingJobTask || invoice) && (
        <Card className="p-4 space-y-4" data-testid="job-invoice-section">
          {/* Section header */}
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-orange-500" />
              Job &amp; Payment
            </h2>
          </div>
          <Separator />

          {/* Job task status row */}
          {existingJobTask && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Job Status</p>
                {existingJobTask.scheduledStart && (
                  <p className="text-xs text-gray-500">
                    {new Date(existingJobTask.scheduledStart).toLocaleString('en-US', {
                      weekday: 'short', month: 'short', day: 'numeric',
                      hour: 'numeric', minute: '2-digit', hour12: true,
                    })}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  existingJobTask.status === 'completed'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {existingJobTask.status === 'completed'
                    ? 'Completed'
                    : existingJobTask.scheduledStart
                    ? 'Scheduled'
                    : 'Ready to Schedule'
                  }
                </span>
                {existingJobTask.status !== 'completed' && (
                  <Button
                    size="sm"
                    onClick={handleMarkComplete}
                    disabled={completing}
                    data-testid="mark-complete-btn-inline"
                    className="bg-purple-600 hover:bg-purple-700 text-white h-7 text-xs"
                  >
                    {completing
                      ? <RefreshCw className="w-3 h-3 animate-spin" />
                      : <CheckCircle2 className="w-3 h-3 mr-1" />}
                    {completing ? '...' : 'Mark Complete'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Create Invoice CTA — shown when job done, no invoice yet */}
          {existingJobTask?.status === 'completed' && !invoice && !invoiceLoading && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-orange-800">Ready to Invoice</p>
                <p className="text-xs text-orange-600">
                  Create an invoice and send the payment link to {getDisplayName(client, 'your customer')}.
                </p>
              </div>
              <Button
                size="sm"
                onClick={handleCreateInvoice}
                disabled={creatingInvoice}
                data-testid="create-invoice-btn-inline"
                className="bg-orange-500 hover:bg-orange-600 text-white shrink-0"
              >
                {creatingInvoice
                  ? <RefreshCw className="w-4 h-4 animate-spin" />
                  : <Receipt className="w-4 h-4 mr-2" />}
                {creatingInvoice ? 'Creating...' : 'Create Invoice'}
              </Button>
            </div>
          )}

          {/* Invoice card — shown when invoice exists */}
          {invoiceLoading && (
            <div className="text-sm text-gray-400 text-center py-2">Loading invoice...</div>
          )}
          {invoice && (
            <div
              className="border rounded-lg overflow-hidden"
              data-testid="invoice-card"
            >
              {/* Invoice header */}
              <div className={`px-4 py-3 flex items-center justify-between ${
                invoice.status === 'paid'
                  ? 'bg-green-50 border-b border-green-200'
                  : 'bg-gray-50 border-b border-gray-200'
              }`}>
                <div className="flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-800">
                    Invoice #{invoice.invoiceId?.slice(-6).toUpperCase()}
                  </span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
                  invoice.status === 'paid'
                    ? 'bg-green-100 text-green-700'
                    : invoice.status === 'sent'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600'
                }`} data-testid="invoice-status-badge">
                  {invoice.status}
                </span>
              </div>

              {/* Invoice details */}
              <div className="px-4 py-3 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Amount</span>
                  <span className="text-lg font-bold text-gray-900" data-testid="invoice-amount">
                    ${parseFloat(invoice.amount || 0).toFixed(2)}
                  </span>
                </div>

                {invoice.status === 'paid' && invoice.paidAt && (
                  <div className="flex items-center gap-2 bg-green-50 text-green-700 rounded-md px-3 py-2 text-xs">
                    <CheckCircle2 className="w-4 h-4" />
                    Paid on {formatDateTime(invoice.paidAt)}
                  </div>
                )}

                {/* Auto-charged via card on file — shown when paymentMethod='card_on_file' */}
                {invoice.status === 'paid' && invoice.paymentMethod === 'card_on_file' && (
                  <div
                    className="flex items-center gap-2 bg-blue-50 text-blue-700 rounded-md px-3 py-2 text-xs"
                    data-testid="auto-charge-badge"
                  >
                    <CreditCard className="w-4 h-4 shrink-0" />
                    Auto-charged via {invoice.cardBrand || 'card'} ••••{invoice.cardLast4 || '****'}
                  </div>
                )}

                {/* Auto-charge attempted but failed — payment link sent as fallback */}
                {invoice.autoChargeFailed && invoice.status !== 'paid' && (
                  <div
                    className="flex items-center gap-2 bg-amber-50 text-amber-700 rounded-md px-3 py-2 text-xs border border-amber-200"
                    data-testid="auto-charge-failed-notice"
                  >
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    Auto-charge failed — payment link sent as fallback
                  </div>
                )}

                {/* Charge Now — universal payment trigger for any unpaid invoice */}
                {invoice.status !== 'paid' && (
                  <Button
                    size="sm"
                    className="w-full h-9 bg-green-600 hover:bg-green-700 text-white font-medium"
                    onClick={() => setChargeNowOpen(true)}
                    data-testid="quote-charge-now-btn"
                  >
                    <DollarSign className="w-3.5 h-3.5 mr-2" />
                    Charge Now
                  </Button>
                )}

                {/* Payment link actions */}
                {invoice.status !== 'paid' && invoice.stripePaymentLink && (
                  <div className="space-y-2 pt-1">
                    <p className="text-xs text-gray-500 font-medium">Send Payment Link</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-9"
                        onClick={handleCopyPaymentLink}
                        data-testid="copy-payment-link-btn"
                      >
                        <Copy className="w-3 h-3 mr-2" />
                        Copy Link
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 h-9 bg-green-600 hover:bg-green-700 text-white"
                        onClick={handleSendInvoiceSms}
                        disabled={sendingSms}
                        data-testid="send-invoice-sms-btn"
                      >
                        {sendingSms
                          ? <RefreshCw className="w-3 h-3 animate-spin mr-2" />
                          : <MessageSquare className="w-3 h-3 mr-2" />}
                        {sendingSms ? 'Sending...' : 'Send SMS'}
                      </Button>
                    </div>
                    <a
                      href={invoice.stripePaymentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-xs text-blue-600 hover:text-blue-800 truncate"
                      data-testid="payment-link-preview"
                    >
                      {invoice.stripePaymentLink}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Printable Preview (hidden by default, shown on print) */}
      <div className="hidden print:block">
        <PrintableQuote
          clientInfo={clientInfoForPrintable}
          services={services}
          totals={totals}
        />
      </div>

      {/* Schedule Job Modal */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-md w-full" data-testid="schedule-job-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-blue-600" />
              {existingJobTask?.scheduledStart ? 'Reschedule Job' : 'Schedule Job'}
            </DialogTitle>
            <DialogDescription>
              Set the date and time for{' '}
              <strong>{getDisplayName(client, 'this customer')}</strong>
              {client.address ? ` · ${formatAddress(client.address)}` : ''}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            {/* Current scheduling info if already scheduled */}
            {existingJobTask?.scheduledStart && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-700">
                Currently scheduled:{' '}
                {new Date(existingJobTask.scheduledStart).toLocaleString('en-US', {
                  weekday: 'short', month: 'short', day: 'numeric',
                  hour: 'numeric', minute: '2-digit', hour12: true,
                })}
              </div>
            )}

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={schedDate}
                min={new Date().toISOString().split('T')[0]}
                onChange={e => setSchedDate(e.target.value)}
                data-testid="schedule-date-input"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Start Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
              <select
                value={schedTime}
                onChange={e => setSchedTime(e.target.value)}
                data-testid="schedule-time-select"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {TIME_SLOTS.map(slot => (
                  <option key={slot.value} value={slot.value}>{slot.label}</option>
                ))}
              </select>
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <select
                value={schedDuration}
                onChange={e => setSchedDuration(Number(e.target.value))}
                data-testid="schedule-duration-select"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {DURATION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Summary */}
            {schedDate && schedTime && (
              <div className="text-xs text-gray-500 bg-gray-50 rounded-md px-3 py-2">
                <span className="font-medium text-gray-700">Scheduled: </span>
                {formatScheduleDisplay(schedDate, schedTime)}
                {' · '}
                {DURATION_OPTIONS.find(o => o.value === schedDuration)?.label || `${schedDuration}min`}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setScheduleOpen(false)}
              disabled={scheduling}
              data-testid="schedule-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleScheduleJob}
              disabled={scheduling || !schedDate || !schedTime}
              data-testid="schedule-confirm-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {scheduling
                ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Scheduling...</>
                : <><CalendarClock className="w-4 h-4 mr-2" />Confirm Schedule</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Service Plan Activation Confirmation Dialog ─────────────────── */}
      {quote?.recurringPlan?.active && (
        <Dialog open={showPlanConfirm} onOpenChange={setShowPlanConfirm}>
          <DialogContent className="sm:max-w-md" data-testid="quote-plan-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="h-5 w-5 text-[var(--cb-orange)]" />
                Confirm Service Plan Activation
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500 pt-1">
                Approving this quote will enroll this client in a recurring service plan.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 my-1">
              {quote.recurringPlan.planLabel && (
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">Plan name</p>
                    <p className="text-sm font-semibold text-gray-900">{quote.recurringPlan.planLabel}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Repeat className="h-4 w-4 text-gray-400 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Frequency</p>
                  <p className="text-sm font-medium text-gray-900">
                    Every {quote.recurringPlan.interval} days
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-gray-400 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500">Duration</p>
                  <p className="text-sm font-medium text-gray-900">
                    {quote.recurringPlan.totalOccurrences
                      ? `${quote.recurringPlan.totalOccurrences} visits`
                      : 'Ongoing (no end date)'}
                  </p>
                </div>
              </div>
              {quote.recurringPlan.startDate && (
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-gray-400 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">First visit</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(quote.recurringPlan.startDate + 'T00:00:00').toLocaleDateString('en-US', {
                        weekday: 'short', month: 'short', day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400 leading-relaxed">
              A first visit task will be added to the Scheduler automatically. Future visits will be projected in Forecast and seeded after each completion.
            </p>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPlanConfirm(false)}
                data-testid="quote-plan-confirm-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => { setShowPlanConfirm(false); handleApproveQuote(); }}
                disabled={approving}
                className="text-white"
                style={{ backgroundColor: 'var(--cb-orange)' }}
                data-testid="quote-plan-confirm-approve"
              >
                {approving
                  ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Approving…</>
                  : <><CheckCircle2 className="w-4 h-4 mr-1.5" />Confirm & Activate Plan</>
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ── Operator-Side Prepay Collection Modal ────────────────────────────── */}
      {showPrepayCollect && quote?.leadId && (
        <CardSetupModal
          mode="charge"
          companyId={quote.companyId}
          leadId={quote.leadId}
          quoteId={quote.quoteId || quoteId}
          prepayAmount={quote.prepayPackage?.packagePrice}
          onSuccess={(invoice) => {
            setPrepayInvoice(invoice);
            setShowPrepayCollect(false);
            toast.success(`Payment collected — ${invoice?.packageName || 'Season Package'} activated.`);
          }}
          onClose={() => setShowPrepayCollect(false)}
        />
      )}

      {/* ── Charge Now Modal ────────────────────────────────────────────────── */}
      {chargeNowOpen && quote?.leadId && (
        <ChargeNowModal
          isOpen={chargeNowOpen}
          onClose={() => setChargeNowOpen(false)}
          companyId={quote.companyId || companyId}
          leadId={quote.leadId}
          customerName={getDisplayName(client, 'Client')}
          invoice={invoice}
          quoteId={quote.quoteId || quoteId}
          onSuccess={() => setChargeNowOpen(false)}
        />
      )}
    </div>
  );
};

export default QuoteDetail;
